/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import axios from 'axios';
import { isLocalStorageAvailable, LocalStorage } from './localStorage';
import { getConfig } from 'blb-utils/config';
export const USE_SECONDARY_AUTH_HEADER_KEY = 'use-secondary-auth';
export const USE_SECONDARY_AUTH_HEADER_VALUE = 'true';
export const USE_SECONDARY_AUTH_QUERY_PARAM_KEY = 'useSecondaryAuth';
export const USE_SECONDARY_AUTH_QUERY_PARAM_VALUE = 'true';
export const USE_SECONDARY_AUTH_LOCALSTORAGE_KEY = 'USE_SECONDARY_AUTH';
export const PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY = 'PRIMARY_AUTH_PRINCIPAL';
export const SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY = 'SECONDARY_AUTH_PRINCIPAL';
// if secondary auth is just set in the server, this cookie should be `true`
export const SECONDARY_AUTH_SET_COOKIE_KEY = 'auth_token_secondary_set';

// Add a request header to instruct server whether to use secondary auth or primary auth
// If the header is not presented in the request, server will use the primary auth toke (auth_token from cookie) instead
export const createAxiosRequestWithSecondaryAuthSupport = config => {
  const axiosInstance = axios.create(config);
  axiosInstance.interceptors.request.use(config => {
    if (LocalStorage.get(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY)) {
      // headers are never undefined on our base request
      config.headers[USE_SECONDARY_AUTH_HEADER_KEY] = USE_SECONDARY_AUTH_HEADER_VALUE;
    }
    return config;
  });
  return axiosInstance;
};

// If we happen to make a request without going through Axios interceptor above (e.g. window.location.href)
// We want to also pass in a flag to to instruct server to use secondary auth
export const updateURLRequestWithSecondaryAuthSupport = url => {
  if (LocalStorage.get(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY)) {
    const fullUrl = new URL(url, window.location.href);
    fullUrl.searchParams.append(USE_SECONDARY_AUTH_QUERY_PARAM_KEY, USE_SECONDARY_AUTH_QUERY_PARAM_VALUE);
    return fullUrl.href;
  }
  return url;
};
export const setSecondaryAuthedUser = me => {
  if (!me) {
    return;
  }
  if (isPrimarySessionSet() && LocalStorage.get(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY)?.principalId === me.principalId) {
    // principal is already set in primary, ignore here.
    return;
  }
  // TODO: set TTL here to align with identity service session TTL
  useSecondarySession();
  LocalStorage.set(SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY, {
    principalId: me.principalId,
    principalType: me.principalType
  });
};
export const setPrimaryAuthedUser = me => {
  if (!me) {
    return;
  }
  // TODO: set TTL here to align with identity service session TTL
  LocalStorage.set(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY, {
    principalId: me.principalId,
    principalType: me.principalType
  });
};
export const clearAllAuthedUsers = () => {
  LocalStorage.remove(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY);
  LocalStorage.remove(SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
  LocalStorage.remove(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
};
export const isSecondaryAuthEnabled = () => {
  const isFeatureAvailable = (getConfig().enableSecondaryAuth ?? false) && isLocalStorageAvailable;
  if (!isFeatureAvailable) {
    return false;
  }
  // if primary session is not a settlement agent or title agent, we want to disable secondary auth
  if (isPrimarySessionSet() && getPrimarySessionUserType() !== 'settlement_agent' && getPrimarySessionUserType() !== 'title_agent') {
    return false;
  }
  return true;
};
export const getPrimarySessionUserType = () => {
  return LocalStorage.get(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY)?.principalType;
};
export const isPrimarySessionSet = () => {
  return !!LocalStorage.get(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
};
export const isSecondarySessionSet = () => {
  return !!LocalStorage.get(SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
};
export const isUsingSecondarySession = () => {
  return LocalStorage.get(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY) || false;
};
export const getPrimarySessionPrincipalId = () => {
  return LocalStorage.get(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY)?.principalType;
};
export const getSecondarySessionPrincipalId = () => {
  return LocalStorage.get(SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY)?.principalType;
};
export const usePrimarySession = () => {
  LocalStorage.remove(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY);
};
export const useSecondarySession = () => {
  LocalStorage.set(USE_SECONDARY_AUTH_LOCALSTORAGE_KEY, true);
};
export const isPrimarySessionActive = () => {
  return isSecondaryAuthEnabled() && isPrimarySessionSet() && !isSecondarySessionSet() && !isUsingSecondarySession();
};

// We use `forSecondary` query param to indicate if this is a request to login with secondary auth
// there are some edge cases where we get a 401 from server and requests get redirected to the regular /login page
// This function returns the flag `true` or undefined if does not exist or not `true`
export const isForSecondaryAuthQueryParam = () => {
  const forSecondaryFlag = new URLSearchParams(location.search).get('forSecondary');
  return forSecondaryFlag === 'true' ? forSecondaryFlag : undefined;
};